import React, { FormEvent, useCallback } from 'react';
import { getTranslation } from '../utils/config';
import FilterPillCategory from './filter-pill-category';
import FilterPillAttributes from './filter-pill-attributes';
import { IFilterContext, useDfContext } from '../context/df-context';
import { RESET_FILTERS, RESET_SELECTION } from '../context/actions';
import { useSearch } from '../hooks/use-search';
import { LoadingRing } from './loading-ring';
import { PAGE_VARIANT, TRANSLATION_SEPARATOR } from '../constants';

export default function FilterModal({
  visible = false,
  handleClose,
  variant,
}: {
  visible: boolean;
  handleClose: () => void;
  variant: string;
}): React.ReactElement {
  const modalContentRef = React.useRef() as React.MutableRefObject<HTMLDivElement>;
  const {
    state: { selected, selectionAmount, isLoading },
    dispatch,
    dispatchResult,
    updateState,
  } = useDfContext<IFilterContext>();

  const applySubmitAction = useSearch();

  const resetFilters = useCallback(() => {
    dispatch({
      type: RESET_SELECTION,
    });
    dispatch({
      type: RESET_FILTERS,
    });
  }, [dispatch]);

  const handleCancel = useCallback(() => {
    handleClose();
    if (variant === PAGE_VARIANT.RESULT) {
      // reset filter context state to result context state
      updateState();
      return;
    }
    resetFilters();
  }, [handleClose, resetFilters, updateState, variant]);

  const closeFlyout = useCallback(
    (e) => {
      // close Flyout on esc
      if (e.keyCode === 27) {
        handleCancel();
      }
    },
    [handleCancel],
  );

  React.useEffect(() => {
    window.addEventListener('keydown', closeFlyout);
    return () => {
      window.removeEventListener('keydown', closeFlyout);
    };
  }, [closeFlyout]);

  React.useEffect(() => {
    if (visible) {
      document.documentElement.classList.add('df-detail-search');
    } else {
      document.documentElement.classList.remove('df-detail-search');
    }
  }, [visible]);

  React.useEffect(() => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTop = 999999;
    }
  }, [selected]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (variant !== PAGE_VARIANT.RESULT) {
      window.location.href = applySubmitAction;
      return;
    }
    dispatchResult();
    handleClose();
  };

  const [beforeResult, afterResult] = getTranslation(
    selectionAmount === 1 ? 'show_result' : 'show_results',
    TRANSLATION_SEPARATOR,
  ).split(TRANSLATION_SEPARATOR);

  return (
    <div className={`df-detail-search-modal ${visible ? 'df-detail-search-modal--visible' : ''}`}>
      <form method="GET" action={applySubmitAction} onSubmit={handleSubmit}>
        <div className="df-detail-search-modal__header__wrapper">
          <div className="df-detail-search-modal__header">
            <strong className="df-detail-search-modal__headline">
              {getTranslation('filter_title')}
            </strong>
            <button className="df-btn-close" type="button" onClick={handleCancel}>
              <span className="hidden">{getTranslation('filter_close')}</span>
            </button>
          </div>
        </div>
        <div className="df-detail-search-modal__content" ref={modalContentRef}>
          <FilterPillAttributes />
          <FilterPillCategory />
        </div>
        <div className="df-detail-search-modal__footer__wrapper">
          <div className="df-detail-search-modal__footer">
            <div className="df-detail-search-modal__footer-flex">
              <button
                className="df-btn df-btn--simple df-btn--cancel"
                type="button"
                onClick={handleCancel}
              >
                <span>{getTranslation('abort')}</span>
              </button>
            </div>
            <div className="df-detail-search-modal__footer-flex">
              <button
                className="df-btn df-btn--simple df-btn--reset"
                type="button"
                onClick={resetFilters}
              >
                <span>{getTranslation('reset_all_filters')}</span>
              </button>
            </div>
            <div className="df-detail-search-modal__footer-flex">
              <button type="submit" className="df-btn">
                {beforeResult && `${beforeResult} `}
                {isLoading ? <LoadingRing /> : selectionAmount}
                {afterResult && ` ${afterResult}`}
                <span className="df-btn__icon-right df-icon-arrow_long_right_white"></span>
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
